import { gql } from 'glimmer-apollo';
import type { TypedDocumentNode } from '@apollo/client/core';

import type {
  SearchAsset,
  SearchGameRef,
  SearchTeamRef,
  SearchPlayerRef,
  SearchSuggestion,
} from '../../types/search';

// A single item returned by the `search` query. The query uses inline fragments
// on several GraphQL types, so this is the union of their fields expressed as one
// optional-field shape; the consumer narrows on `__typename` at runtime.
export interface RawSearchResultItem {
  __typename?: string;
  id: string;

  // Articles (ESportsArticle | GameArticle)
  title?: string | null;
  shortDescription?: string | null;
  slug?: string | null;
  publishedDate?: string | null;
  coverImage?: SearchAsset | null;
  author?: { name?: string | null; avatar?: { url?: string | null } | null } | null;
  // Not selected by the query, but read defensively by the transform.
  tags?: string[] | null;

  // Teams
  name?: string | null;
  bioBlurb?: string | null;
  country?: string | null;
  region?: string | null;
  logo?: SearchAsset | null;
  game?: SearchGameRef | null;
  players?: SearchPlayerRef[] | null;

  // Players
  gamertag?: string | null;
  irlName?: string | null;
  role?: string | null;
  photo?: SearchAsset | null;
  currentTeam?: SearchTeamRef | null;

  // Tournaments
  startDate?: string | null;
  endDate?: string | null;
  prizePool?: number | null;
  format?: string | null;
  image?: SearchAsset | null;

  // Matches
  teams?: SearchTeamRef[] | null;
}

export interface SearchContentResult {
  search: {
    results: RawSearchResultItem[];
  } | null;
}

export interface SearchContentVariables {
  query: string;
  limit?: number | null;
}

export interface SearchSuggestionsResult {
  searchSuggestions: SearchSuggestion[] | null;
}

export interface SearchSuggestionsVariables {
  query: string;
  limit?: number | null;
}

export const SEARCH_QUERY: TypedDocumentNode<
  SearchContentResult,
  SearchContentVariables
> = gql`
  query SearchContent($query: String!, $limit: Int) {
    search(query: $query, limit: $limit) {
      results {
        ... on ESportsArticle {
          __typename
          id
          title
          shortDescription
          slug
          publishedDate
          coverImage {
            url
            title
            description
          }
          author {
            name
            avatar {
              url
            }
          }
        }
        ... on GameArticle {
          __typename
          id
          title
          shortDescription
          slug
          publishedDate
          coverImage {
            url
            title
            description
          }
          author {
            name
            avatar {
              url
            }
          }
        }
        ... on Team {
          __typename
          id
          name
          slug
          bioBlurb
          country
          region
          logo {
            url
            title
            description
          }
          game {
            id
            name
            image {
              url
            }
          }
          players {
            id
            gamertag
            slug
          }
        }
        ... on Player {
          __typename
          id
          gamertag
          irlName
          slug
          country
          role
          photo {
            url
            title
            description
          }
          currentTeam {
            id
            name
            slug
            logo {
              url
            }
          }
        }
        ... on Tournament {
          __typename
          id
          title
          slug
          startDate
          endDate
          prizePool
          format
          image {
            url
          }
          game {
            id
            name
            image {
              url
              title
              description
            }
          }
        }
        ... on Match {
          __typename
          id
          title
          slug
          format
          game {
            id
            name
            image {
              url
              title
              description
            }
          }
          teams {
            id
            name
            slug
            logo {
              url
              title
              description
            }
          }
        }
      }
    }
  }
`;

export const SEARCH_SUGGESTIONS_QUERY: TypedDocumentNode<
  SearchSuggestionsResult,
  SearchSuggestionsVariables
> = gql`
  query SearchSuggestions($query: String!, $limit: Int) {
    searchSuggestions(query: $query, limit: $limit) {
      text
      type
      count
    }
  }
`;